import { Node, mergeAttributes } from "@tiptap/core";
import { ReactNodeViewRenderer, NodeViewWrapper } from "@tiptap/react";
import React from "react";

/**
 * Full-bleed branded cover page. Atom node configured via a dialog; renders the
 * complete .doc-cover markup (swoosh + logo + title + footer band) whose colours
 * come from the document theme. Shown as a friendly placeholder in the editor.
 */
const CoverComponent = ({ node, getPos, extension }) => {
  const { title, subtitle, footerHeading, footerText } = node.attrs;

  // In read-only contexts (e.g. the review editor) the cover is a render-only
  // artifact — keep the node in the document (so it appears in the saved PDF)
  // but don't show its editing placeholder.
  if (extension && extension.options && extension.options.hidden) {
    return (
      <NodeViewWrapper
        className="template-doc-cover-hidden"
        contentEditable={false}
        style={{ display: "none" }}
      />
    );
  }

  const handleClick = (e) => {
    e.stopPropagation();
    if (extension && extension.options && extension.options.onEdit) {
      extension.options.onEdit({
        position: getPos(),
        current: { title, subtitle, footerHeading, footerText },
      });
    }
  };
  return (
    <NodeViewWrapper className="tip-card" contentEditable={false} onClick={handleClick}>
      <div className="tip-card-head">
        <i className="fa fa-id-card tip-card-icon"></i>
        <span className="tip-card-title">Cover Page</span>
        <span className="tip-card-edit">
          <i className="fa fa-pen"></i> Edit
        </span>
      </div>
      <div className="tip-card-title-lg">{title || "(cover title)"}</div>
      {subtitle ? <div className="tip-card-sub">{subtitle}</div> : null}
    </NodeViewWrapper>
  );
};

export const DocCover = Node.create({
  name: "docCover",
  group: "block",
  atom: true,

  addOptions() {
    return { onEdit: null, hidden: false };
  },

  addAttributes() {
    // Each field round-trips via a data-* attribute on the root .doc-cover div.
    return {
      title: {
        default: "",
        parseHTML: (el) => el.getAttribute("data-title") || "",
        renderHTML: (a) => (a.title ? { "data-title": a.title } : {}),
      },
      subtitle: {
        default: "",
        parseHTML: (el) => el.getAttribute("data-subtitle") || "",
        renderHTML: (a) => (a.subtitle ? { "data-subtitle": a.subtitle } : {}),
      },
      footerHeading: {
        default: "Health Screening",
        parseHTML: (el) => el.getAttribute("data-footer-heading") || "",
        renderHTML: (a) =>
          a.footerHeading ? { "data-footer-heading": a.footerHeading } : {},
      },
      footerText: {
        default: "",
        parseHTML: (el) => el.getAttribute("data-footer-text") || "",
        renderHTML: (a) =>
          a.footerText ? { "data-footer-text": a.footerText } : {},
      },
    };
  },

  parseHTML() {
    return [{ tag: "div.doc-cover" }];
  },

  renderHTML({ node, HTMLAttributes }) {
    const a = node.attrs;
    return [
      "div",
      mergeAttributes(HTMLAttributes, { class: "doc-cover" }),
      ["div", { class: "swoosh-tr" }],
      [
        "div",
        { class: "swoosh-b" },
        ["div", { class: "w-soft" }],
        ["div", { class: "w1" }],
        ["div", { class: "w2" }],
      ],
      ["div", { class: "cover-logo" }],
      [
        "div",
        { class: "cover-title" },
        ["h1", {}, a.title || ""],
        ["div", { class: "sub" }, a.subtitle || ""],
      ],
      [
        "div",
        { class: "cover-foot" },
        ["div", { class: "big" }, a.footerHeading || ""],
        ["div", { class: "small" }, a.footerText || ""],
      ],
    ];
  },

  addNodeView() {
    return ReactNodeViewRenderer(CoverComponent);
  },

  addCommands() {
    return {
      insertDocCover:
        (attrs) =>
        ({ commands }) =>
          commands.insertContent({ type: this.name, attrs }),
    };
  },
});

export default DocCover;
